/**
 * AutoUpdateModal — update-download confirmation dialog.
 *
 * Triggered by the `confirm-update-download` Tauri IPC event.
 * Lets the user accept or dismiss an available application update.
 * On acceptance, Tauri handles the download and installation.
 */
import Modal, { Styles } from "react-modal";
import { HiOutlineDownload } from "react-icons/hi";
import { MdClose } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import { sendUpdateDownloadConfirmation } from "@service/ipc-handler";
import { confirmDownloadReducer } from "@store/ipc-events-modal-splice";
import { getColorTheme } from "@service/local-storage-handler";
import { platform } from "@platform";

import "./auto-update-modal.css";

Modal.setAppElement("#marketview");

const AutoUpdateModal = () => {
  const dispatch = useDispatch();
  const themeFromStore = useSelector(({ theme }) => theme.theme);
  // Default to dark if theme is not set in Redux (fallback to localStorage)
  const theme = themeFromStore || getColorTheme() || "dark";

  const { confirmDownload } = useSelector(
    ({ ipcEventsModal }) => ipcEventsModal
  );

  const customStyles: Styles = {
    overlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(0, 0, 0, 0.75)",
      zIndex: 1000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    content: {
      position: "relative",
      top: "auto",
      left: "auto",
      right: "auto",
      bottom: "auto",
      border: "none",
      background: "transparent",
      overflow: "visible",
      WebkitOverflowScrolling: "touch",
      borderRadius: "16px",
      outline: "none",
      padding: 0,
      maxWidth: "400px",
      width: "90%",
    },
  };

  const closeModal = () => {
    sendUpdateDownloadConfirmation(false);
    dispatch(confirmDownloadReducer({ confirmDownload: false }));
  };

  const handleCancelButton = () => {
    closeModal();
    // Quit the app — update is mandatory
    platform.closeWindow().catch(console.error);
  };

  const handleUpdateButton = () => {
    sendUpdateDownloadConfirmation(true);
    dispatch(confirmDownloadReducer({ confirmDownload: false }));
  };

  return (
    <Modal isOpen={confirmDownload} style={customStyles}>
      <div className={`auto-update-wrapper ${theme}`}>
        <div className="update-icon-container">
          <HiOutlineDownload className="update-icon" />
        </div>
        <h3 className="update-title">Update Available</h3>
        <p className="update-message">
          A new version of MarketView is available. Would you like to download
          and install it now?
        </p>
        <div className="update-btn-container">
          <button
            type="button"
            className="update-cancel-btn"
            onClick={handleCancelButton}
          >
            Later
          </button>
          <button
            type="button"
            className="update-confirm-btn"
            onClick={handleUpdateButton}
          >
            Update Now
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AutoUpdateModal;
